import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import swal from "sweetalert";
import { getMenuLanguages } from "../../../../../constants/menuLanguages";
import { showWizardToast, showWizardValidation } from "../wizardFeedback";
import useDragAutoScroll from "../../../Cms/MenuTree/useDragAutoScroll";
import {
  makeTempId,
  getPrimaryMenuLanguage,
  getMenuOptionalLanguages,
  getCategoryDisplayTitle,
  getSubcategoryDisplayTitle,
  buildCategoryLocalizedDetails,
  buildSubcategoryLocalizedDetails,
  categoryToFormState,
  subcategoryToFormState,
  reorderIdList,
} from "./utils";

const ExtraPanel = ({ open, onToggle, title, meta, children }) => (
  <div className={`start-step1-extra${open ? " is-open" : ""}`}>
    <button
      type="button"
      className="start-step1-extra__toggle"
      onClick={onToggle}
      aria-expanded={open}
    >
      <span className="start-step1-extra__chevron" aria-hidden="true">
        <i className="fa fa-chevron-down" />
      </span>
      <span className="start-step1-extra__title">{title}</span>
      {meta ? <span className="start-step1-extra__meta">{meta}</span> : null}
    </button>
    {open ? <div className="start-step1-extra__body">{children}</div> : null}
  </div>
);

const Step5CategoriesSubcategories = ({ draft, setDraft, goNext, goPrev }) => {
  const { t } = useTranslation();
  const allLanguages = getMenuLanguages(t);
  const menuPrimaryLanguage = getPrimaryMenuLanguage(draft?.menu);
  const menuOptionalLanguages = getMenuOptionalLanguages(draft?.menu);
  const primaryLangMeta = allLanguages.find((lang) => lang.code === menuPrimaryLanguage);

  const [categoryTitles, setCategoryTitles] = useState({});
  const [subcategoryDrafts, setSubcategoryDrafts] = useState({});
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [editingSubcategory, setEditingSubcategory] = useState(null);
  const [draggingCategoryId, setDraggingCategoryId] = useState(null);
  const [draggingSubcategory, setDraggingSubcategory] = useState(null);
  const [languagesOpen, setLanguagesOpen] = useState(false);
  const [treeOpen, setTreeOpen] = useState(true);
  const categoryFormRef = useRef(null);
  const autoScroll = useDragAutoScroll({ moveEvent: "dragover" });

  const primaryCategoryTitle = categoryTitles[menuPrimaryLanguage] || "";

  const categoriesOrdered = useMemo(() => {
    const order = Array.isArray(draft.relations?.menuCategories)
      ? draft.relations.menuCategories
      : [];
    const byId = new Map((draft.categories || []).map((c) => [c.tempId, c]));
    return order.map((id) => byId.get(id)).filter(Boolean);
  }, [draft.categories, draft.relations?.menuCategories]);

  const canSaveCategory = useMemo(() => primaryCategoryTitle.trim().length > 0, [primaryCategoryTitle]);
  const canNext = useMemo(() => (draft.categories || []).length > 0, [draft.categories]);

  const getSubcategoryDraft = (categoryTempId) => subcategoryDrafts[categoryTempId] || {};

  const getSubcategoryPrimaryTitle = (categoryTempId) =>
    getSubcategoryDraft(categoryTempId)[menuPrimaryLanguage] || "";

  const updateCategoryTitle = (language, value) => {
    setCategoryTitles((prev) => ({ ...prev, [language]: value }));
  };

  const updateSubcategoryTitle = (categoryTempId, language, value) => {
    setSubcategoryDrafts((prev) => ({
      ...prev,
      [categoryTempId]: {
        ...(prev[categoryTempId] || {}),
        [language]: value,
      },
    }));
  };

  const resetCategoryForm = () => {
    setCategoryTitles({});
    setEditingCategoryId(null);
  };

  const resetSubcategoryForm = (categoryTempId) => {
    if (categoryTempId) {
      setSubcategoryDrafts((prev) => ({ ...prev, [categoryTempId]: {} }));
    }
    setEditingSubcategory(null);
  };

  const getSubcategoriesForCategory = (categoryTempId) => {
    const order = draft.relations?.categorySubcategories?.[categoryTempId] || [];
    const byId = new Map((draft.subcategories || []).map((s) => [s.tempId, s]));
    return order.map((id) => byId.get(id)).filter(Boolean);
  };

  const saveCategory = () => {
    if (!primaryCategoryTitle.trim()) {
      showWizardValidation(t, "wizardCategoryTitleRequired");
      return;
    }

    const categoryDetails = buildCategoryLocalizedDetails({
      primaryLanguage: menuPrimaryLanguage,
      optionalLanguages: menuOptionalLanguages,
      titles: categoryTitles,
    });

    if (editingCategoryId) {
      setDraft((prev) => ({
        ...prev,
        categories: (prev.categories || []).map((category) =>
          category.tempId === editingCategoryId ? { ...category, categoryDetails } : category
        ),
      }));
      resetCategoryForm();
      showWizardToast(t, "wizardCategoryUpdated");
      return;
    }

    const category = {
      tempId: makeTempId("category"),
      categoryDetails,
      displayInCategoryList: true,
      displayOrientation: "vertical",
    };

    setDraft((prev) => ({
      ...prev,
      categories: [...(prev.categories || []), category],
      relations: {
        ...prev.relations,
        menuCategories: [...(prev.relations?.menuCategories || []), category.tempId],
        categorySubcategories: {
          ...(prev.relations?.categorySubcategories || {}),
          [category.tempId]: [],
        },
        categoryProducts: {
          ...(prev.relations?.categoryProducts || {}),
          [category.tempId]: [],
        },
      },
    }));
    setCategoryTitles({});
    showWizardToast(t, "wizardCategoryAdded");
  };

  const saveSubcategory = (categoryTempId) => {
    const titles = getSubcategoryDraft(categoryTempId);
    const primaryTitle = String(titles[menuPrimaryLanguage] || "").trim();
    if (!primaryTitle || !categoryTempId) {
      showWizardValidation(t, "wizardSubcategoryTitleRequired");
      return;
    }

    const subCategoryDetails = buildSubcategoryLocalizedDetails({
      primaryLanguage: menuPrimaryLanguage,
      optionalLanguages: menuOptionalLanguages,
      titles,
    });

    if (
      editingSubcategory?.id &&
      editingSubcategory.categoryId === categoryTempId
    ) {
      setDraft((prev) => ({
        ...prev,
        subcategories: (prev.subcategories || []).map((subcategory) =>
          subcategory.tempId === editingSubcategory.id
            ? { ...subcategory, subCategoryDetails }
            : subcategory
        ),
      }));
      resetSubcategoryForm(categoryTempId);
      showWizardToast(t, "wizardSubcategoryUpdated");
      return;
    }

    const subcategory = {
      tempId: makeTempId("subcategory"),
      subCategoryDetails,
      displayOrientation: "vertical",
      categoryTempId,
    };

    setDraft((prev) => ({
      ...prev,
      subcategories: [...(prev.subcategories || []), subcategory],
      relations: {
        ...prev.relations,
        categorySubcategories: {
          ...(prev.relations?.categorySubcategories || {}),
          [categoryTempId]: [
            ...(prev.relations?.categorySubcategories?.[categoryTempId] || []),
            subcategory.tempId,
          ],
        },
        subcategoryProducts: {
          ...(prev.relations?.subcategoryProducts || {}),
          [subcategory.tempId]: [],
        },
      },
    }));

    setSubcategoryDrafts((prev) => ({ ...prev, [categoryTempId]: {} }));
    showWizardToast(t, "wizardSubcategoryAdded");
  };

  const startEditCategory = (category) => {
    if (editingSubcategory?.categoryId) {
      resetSubcategoryForm(editingSubcategory.categoryId);
    }
    const { titles } = categoryToFormState(category);
    setEditingCategoryId(category.tempId);
    setCategoryTitles(titles);
  };

  const startEditSubcategory = (subcategory, categoryTempId) => {
    resetCategoryForm();
    const { titles } = subcategoryToFormState(subcategory);
    setEditingSubcategory({ id: subcategory.tempId, categoryId: categoryTempId });
    setSubcategoryDrafts((prev) => ({ ...prev, [categoryTempId]: titles }));
  };

  const removeCategory = async (categoryTempId) => {
    const confirmed = await swal({
      title: t("areYouSure") || "Are you sure?",
      text: t("wizardDeleteCategoryConfirm") || "Delete this category and its subcategories?",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });
    if (!confirmed) return;

    if (editingCategoryId === categoryTempId) {
      resetCategoryForm();
    }
    if (editingSubcategory?.categoryId === categoryTempId) {
      resetSubcategoryForm(categoryTempId);
    }

    const subIds = draft.relations?.categorySubcategories?.[categoryTempId] || [];

    setDraft((prev) => {
      const nextSubcategoryProducts = { ...(prev.relations?.subcategoryProducts || {}) };
      subIds.forEach((sid) => {
        delete nextSubcategoryProducts[sid];
      });

      const nextCategorySubcategories = { ...(prev.relations?.categorySubcategories || {}) };
      delete nextCategorySubcategories[categoryTempId];

      const nextCategoryProducts = { ...(prev.relations?.categoryProducts || {}) };
      delete nextCategoryProducts[categoryTempId];

      return {
        ...prev,
        categories: (prev.categories || []).filter((c) => c.tempId !== categoryTempId),
        subcategories: (prev.subcategories || []).filter((s) => s.categoryTempId !== categoryTempId),
        relations: {
          ...prev.relations,
          menuCategories: (prev.relations?.menuCategories || []).filter((id) => id !== categoryTempId),
          categorySubcategories: nextCategorySubcategories,
          categoryProducts: nextCategoryProducts,
          subcategoryProducts: nextSubcategoryProducts,
        },
      };
    });

    setSubcategoryDrafts((prev) => {
      const next = { ...prev };
      delete next[categoryTempId];
      return next;
    });
    showWizardToast(t, "wizardCategoryDeleted");
  };

  const removeSubcategory = async (subcategoryTempId, categoryTempId) => {
    const confirmed = await swal({
      title: t("areYouSure") || "Are you sure?",
      text: t("wizardDeleteSubcategoryConfirm") || "Delete this subcategory?",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });
    if (!confirmed) return;

    if (editingSubcategory?.id === subcategoryTempId) {
      resetSubcategoryForm(categoryTempId);
    }

    setDraft((prev) => {
      const nextSubcategoryProducts = { ...(prev.relations?.subcategoryProducts || {}) };
      delete nextSubcategoryProducts[subcategoryTempId];

      return {
        ...prev,
        subcategories: (prev.subcategories || []).filter((s) => s.tempId !== subcategoryTempId),
        relations: {
          ...prev.relations,
          categorySubcategories: {
            ...(prev.relations?.categorySubcategories || {}),
            [categoryTempId]: (prev.relations?.categorySubcategories?.[categoryTempId] || []).filter(
              (id) => id !== subcategoryTempId
            ),
          },
          subcategoryProducts: nextSubcategoryProducts,
        },
      };
    });
    showWizardToast(t, "wizardSubcategoryDeleted");
  };

  const beginCategoryDrag = (event, categoryId) => {
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", categoryId);
    setDraggingCategoryId(categoryId);
    autoScroll.onDragStart();
  };

  const endCategoryDrag = () => {
    autoScroll.onDragEnd();
    setDraggingCategoryId(null);
  };

  const beginSubcategoryDrag = (event, payload) => {
    event.stopPropagation();
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", payload.id);
    setDraggingSubcategory(payload);
    autoScroll.onDragStart();
  };

  const endSubcategoryDrag = (event) => {
    event.stopPropagation();
    autoScroll.onDragEnd();
    setDraggingSubcategory(null);
  };

  const allowDragOver = (event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  };

  useEffect(() => {
    if (!draggingCategoryId && !draggingSubcategory) return undefined;
    const keepDragAlive = (event) => {
      event.preventDefault();
    };
    document.addEventListener("dragover", keepDragAlive);
    return () => document.removeEventListener("dragover", keepDragAlive);
  }, [draggingCategoryId, draggingSubcategory]);

  const handleCategoryDrop = (targetCategoryId) => {
    if (!draggingCategoryId || draggingCategoryId === targetCategoryId) return;
    setDraft((prev) => ({
      ...prev,
      relations: {
        ...prev.relations,
        menuCategories: reorderIdList(
          prev.relations?.menuCategories || [],
          draggingCategoryId,
          targetCategoryId
        ),
      },
    }));
    setDraggingCategoryId(null);
    showWizardToast(t, "wizardCategoryOrderUpdated");
  };

  const handleSubcategoryDrop = (categoryTempId, targetSubcategoryId) => {
    if (!draggingSubcategory || draggingSubcategory.categoryId !== categoryTempId) return;
    if (draggingSubcategory.id === targetSubcategoryId) return;

    setDraft((prev) => ({
      ...prev,
      relations: {
        ...prev.relations,
        categorySubcategories: {
          ...(prev.relations?.categorySubcategories || {}),
          [categoryTempId]: reorderIdList(
            prev.relations?.categorySubcategories?.[categoryTempId] || [],
            draggingSubcategory.id,
            targetSubcategoryId
          ),
        },
      },
    }));
    setDraggingSubcategory(null);
    showWizardToast(t, "wizardSubcategoryOrderUpdated");
  };

  useEffect(() => {
    if (!editingCategoryId || !categoryFormRef.current) return;

    categoryFormRef.current.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });

    const titleInput = categoryFormRef.current.querySelector("input.form-control");
    titleInput?.focus({ preventScroll: true });
  }, [editingCategoryId]);

  const renderOptionalTitleFields = ({ values, onChange, titleLabel, placeholder }) => {
    if (menuOptionalLanguages.length === 0) return null;

    return (
      <div className="start-optional-titles">
        {menuOptionalLanguages.map((code) => {
          const lang = allLanguages.find((item) => item.code === code);
          if (!lang) return null;
          return (
            <div key={code} className="start-optional-title-row">
              <label className="start-step1-label">
                {titleLabel}
                <span className="start-step1-label__lang">{lang.label}</span>
              </label>
              <input
                type="text"
                className="form-control start-input"
                placeholder={placeholder}
                value={values[code] || ""}
                onChange={(e) => onChange(code, e.target.value)}
              />
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <div
        className="mt-3 start-step-content"
        ref={autoScroll.rootRef}
        onDragOver={allowDragOver}
      >
        <div className="start-step-hero">
          <h2 className="start-step-title">
            <span className="step-badge">6</span>
            {t("wizardCreateCategoriesHeading")}
          </h2>
          <p className="start-step-subtitle">{t("wizardCreateCategoriesSub")}</p>
        </div>

        <div className="start-options">
          <section
            className="start-products-form start-step-hero-scroll-target"
            ref={categoryFormRef}
            aria-label={editingCategoryId ? t("wizardEditCategoryLabel") : t("wizardAddCategoryLabel")}
          >
            <div className="start-options-section-head">
              <span className="start-options-section-head__index">1</span>
              <div>
                <h3 className="start-options-section-head__title">
                  {editingCategoryId ? t("wizardEditCategoryLabel") : t("wizardAddCategoryLabel")}
                </h3>
                <p className="start-options-section-head__hint">
                  {editingCategoryId ? t("wizardEditCategoryHint") : t("wizardAddCategoryHint")}
                </p>
              </div>
            </div>

            <div>
              <label className="start-step1-label" htmlFor="wizard-category-title">
                {t("wizardCategoryTitleLabel")}
                <span className="start-step1-required" aria-hidden="true">*</span>
                {primaryLangMeta ? (
                  <span className="start-step1-label__lang">{primaryLangMeta.label}</span>
                ) : null}
              </label>
              <input
                id="wizard-category-title"
                className="form-control start-input"
                placeholder={t("wizardCategoryTitlePlaceholder")}
                value={categoryTitles[menuPrimaryLanguage] || ""}
                onChange={(e) => updateCategoryTitle(menuPrimaryLanguage, e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && canSaveCategory) saveCategory();
                }}
              />
            </div>

            {menuOptionalLanguages.length > 0 ? (
              <ExtraPanel
                open={languagesOpen}
                onToggle={() => setLanguagesOpen((v) => !v)}
                title={t("wizardOptionalLanguagesLabel")}
                meta={t("optional")}
              >
                <p className="start-step1-extra__hint">{t("wizardCategoryOptionalLanguagesFromStep1")}</p>
                {renderOptionalTitleFields({
                  values: categoryTitles,
                  onChange: updateCategoryTitle,
                  titleLabel: t("wizardCategoryTitleLabel"),
                  placeholder: t("wizardCategoryTitlePlaceholder"),
                })}
              </ExtraPanel>
            ) : null}

            <div className="start-products-form__actions">
              <button
                type="button"
                className="start-products-add"
                disabled={!canSaveCategory}
                onClick={saveCategory}
              >
                {editingCategoryId ? t("wizardUpdateCategory") : t("wizardAddCategoryButton")}
              </button>
              {editingCategoryId ? (
                <button type="button" className="start-static-btn" onClick={resetCategoryForm}>
                  {t("cancel")}
                </button>
              ) : null}
            </div>
          </section>

          <section
            className={`start-products-added${treeOpen ? " is-open" : ""}`}
            aria-label={t("wizardYourCategories")}
          >
            <button
              type="button"
              className="start-products-added__toggle"
              onClick={() => setTreeOpen((open) => !open)}
              aria-expanded={treeOpen}
            >
              <span className="start-options-section-head__index">2</span>
              <h3 className="start-products-added__title">{t("wizardYourCategories")}</h3>
              <span className="start-products-added__count">{categoriesOrdered.length}</span>
              <span className="start-products-added__chevron" aria-hidden="true">
                <i className="fa fa-chevron-down" />
              </span>
            </button>
            {treeOpen ? (
              <div className="start-products-added__body">
                <p className="start-options-hint">{t("wizardCategoriesDragHint")}</p>
                {categoriesOrdered.length === 0 ? (
                  <p className="start-products-empty">{t("wizardCategoriesEmpty")}</p>
                ) : (
                  <div className="start-category-tree">
                    {categoriesOrdered.map((category) => {
                      const subcategories = getSubcategoriesForCategory(category.tempId);
                      const subDraft = getSubcategoryDraft(category.tempId);
                      const subPrimaryTitle = getSubcategoryPrimaryTitle(category.tempId);
                      const isEditingSubcategoryHere =
                        editingSubcategory?.categoryId === category.tempId;

                      return (
                        <article
                          key={category.tempId}
                          className={`start-category-card${
                            draggingCategoryId === category.tempId ? " is-dragging" : ""
                          }${editingCategoryId === category.tempId ? " is-editing" : ""}`}
                          draggable
                          onDragStart={(event) => beginCategoryDrag(event, category.tempId)}
                          onDragEnd={endCategoryDrag}
                          onDragOver={allowDragOver}
                          onDrop={(event) => {
                            event.preventDefault();
                            handleCategoryDrop(category.tempId);
                          }}
                        >
                          <div className="start-category-card__header">
                            <span className="start-category-card__drag" aria-hidden="true">
                              <i className="fa fa-grip-vertical" />
                            </span>
                            <div className="start-category-card__title">
                              {getCategoryDisplayTitle(category, menuPrimaryLanguage)}
                            </div>
                            <div className="start-category-card__actions">
                              <button
                                type="button"
                                className="start-products-row__icon-btn is-edit"
                                onClick={() => startEditCategory(category)}
                                aria-label={t("edit")}
                                title={t("edit")}
                              >
                                <i className="fa fa-pen" aria-hidden="true" />
                              </button>
                              <button
                                type="button"
                                className="start-products-row__icon-btn is-danger"
                                onClick={() => removeCategory(category.tempId)}
                                aria-label={t("remove")}
                                title={t("remove")}
                              >
                                <i className="fa fa-trash-alt" aria-hidden="true" />
                              </button>
                            </div>
                          </div>

                          <div className="start-category-card__subs">
                            <div className="start-category-card__subs-head">
                              <span className="start-products-row__extra-label">
                                {t("wizardLinkSubcategoriesLabel")}
                              </span>
                              <span className="start-products-added__count">{subcategories.length}</span>
                            </div>

                            {subcategories.length === 0 ? (
                              <p className="start-subcategory-empty">{t("wizardNoSubcategoriesYet")}</p>
                            ) : (
                              <div className="start-subcategory-list">
                                {subcategories.map((subcategory) => (
                                  <div
                                    key={subcategory.tempId}
                                    className={`start-subcategory-row${
                                      draggingSubcategory?.id === subcategory.tempId ? " is-dragging" : ""
                                    }${
                                      editingSubcategory?.id === subcategory.tempId ? " is-editing" : ""
                                    }`}
                                    draggable
                                    onDragStart={(event) =>
                                      beginSubcategoryDrag(event, {
                                        id: subcategory.tempId,
                                        categoryId: category.tempId,
                                      })
                                    }
                                    onDragEnd={endSubcategoryDrag}
                                    onDragOver={allowDragOver}
                                    onDrop={(event) => {
                                      event.preventDefault();
                                      event.stopPropagation();
                                      handleSubcategoryDrop(category.tempId, subcategory.tempId);
                                    }}
                                  >
                                    <span className="start-subcategory-row__drag" aria-hidden="true">
                                      <i className="fa fa-grip-lines" />
                                    </span>
                                    <span className="start-subcategory-row__title">
                                      {getSubcategoryDisplayTitle(subcategory, menuPrimaryLanguage)}
                                    </span>
                                    <div className="start-subcategory-row__actions">
                                      <button
                                        type="button"
                                        className="start-products-row__icon-btn is-edit"
                                        onClick={() => startEditSubcategory(subcategory, category.tempId)}
                                        aria-label={t("edit")}
                                        title={t("edit")}
                                      >
                                        <i className="fa fa-pen" aria-hidden="true" />
                                      </button>
                                      <button
                                        type="button"
                                        className="start-products-row__icon-btn is-danger"
                                        onClick={() =>
                                          removeSubcategory(subcategory.tempId, category.tempId)
                                        }
                                        aria-label={t("remove")}
                                        title={t("remove")}
                                      >
                                        <i className="fa fa-trash-alt" aria-hidden="true" />
                                      </button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}

                            <div className="start-subcategory-add-block">
                              <label className="start-step1-label" htmlFor={`wizard-sub-${category.tempId}`}>
                                {isEditingSubcategoryHere
                                  ? t("wizardEditSubcategoryLabel")
                                  : t("wizardSubcategoryTitleLabel")}
                                {primaryLangMeta ? (
                                  <span className="start-step1-label__lang">{primaryLangMeta.label}</span>
                                ) : null}
                                <span className="start-step1-label__lang">{t("optional")}</span>
                              </label>
                              <div className="start-subcategory-add-row">
                                <input
                                  id={`wizard-sub-${category.tempId}`}
                                  className="form-control start-input"
                                  placeholder={t("wizardSubcategoryTitlePlaceholder")}
                                  value={subDraft[menuPrimaryLanguage] || ""}
                                  onChange={(e) =>
                                    updateSubcategoryTitle(
                                      category.tempId,
                                      menuPrimaryLanguage,
                                      e.target.value
                                    )
                                  }
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" && subPrimaryTitle.trim()) {
                                      saveSubcategory(category.tempId);
                                    }
                                  }}
                                />
                                <button
                                  type="button"
                                  className="start-subcategory-add-btn"
                                  disabled={!subPrimaryTitle.trim()}
                                  onClick={() => saveSubcategory(category.tempId)}
                                >
                                  {isEditingSubcategoryHere
                                    ? t("wizardUpdateSubcategory")
                                    : t("wizardAddSubcategoryButton")}
                                </button>
                                {isEditingSubcategoryHere ? (
                                  <button
                                    type="button"
                                    className="start-static-btn"
                                    onClick={() => resetSubcategoryForm(category.tempId)}
                                  >
                                    {t("cancel")}
                                  </button>
                                ) : null}
                              </div>
                              {menuOptionalLanguages.length > 0
                                ? renderOptionalTitleFields({
                                    values: subDraft,
                                    onChange: (code, value) =>
                                      updateSubcategoryTitle(category.tempId, code, value),
                                    titleLabel: t("wizardSubcategoryTitleLabel"),
                                    placeholder: t("wizardSubcategoryTitlePlaceholder"),
                                  })
                                : null}
                            </div>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : null}
          </section>
        </div>
      </div>

      <div className="toolbar toolbar-bottom start-toolbar">
        <button className="btn btn-secondary sw-btn-prev me-1 start-btn-back" onClick={goPrev} type="button">
          ← {t("back")}
        </button>
        <div className="wizard-step-counter">{t("wizardStep6of9")}</div>
        <button
          className="btn btn-primary sw-btn-next ms-1 start-btn-next"
          disabled={!canNext}
          onClick={goNext}
          type="button"
        >
          {t("wizardContinue")} <span aria-hidden="true">→</span>
        </button>
      </div>
    </>
  );
};

export default Step5CategoriesSubcategories;
